import { WebInputChoice } from "@p0security/shared/integrations/notifiers/web-requests/types";
import type { SelectProps } from "antd";
import { Select, Spin } from "antd";
import debounce from "lodash/debounce";
import React, { useCallback, useMemo, useRef, useState } from "react";

import { NoContentFound } from "./NoContentFound";
import { createOptionGroups } from "./Utils";

export type BaseSelectProps = Omit<
  SelectProps<WebInputChoice | WebInputChoice[]>,
  "children" | "options" | "ref"
>;

const MIN_CHARS_FOR_SEARCH = 3;

export interface DebounceSelectProps extends BaseSelectProps {
  fetchOptions: (search: string) => Promise<WebInputChoice[]>;
  debounceTimeout?: number;
  minCharsForSearch?: number;
}

const DebounceSelect = React.forwardRef(
  (
    {
      fetchOptions,
      debounceTimeout = 500,
      minCharsForSearch = MIN_CHARS_FOR_SEARCH,
      ...props
    }: DebounceSelectProps,
    ref: React.ForwardedRef<any>
  ) => {
    const [nascent, setNascent] = useState(true);
    const [search, setSearch] = useState("");
    const [fetching, setFetching] = useState(false);
    const [options, setOptions] = useState<WebInputChoice[]>([]);
    const fetchRef = useRef(0);

    const debounceFetcher = useMemo(() => {
      const loadOptions = (value: string) => {
        fetchRef.current += 1;
        const fetchId = fetchRef.current;
        setOptions([]);
        setFetching(true);

        fetchOptions(value).then((newOptions) => {
          if (fetchId !== fetchRef.current) {
            // for fetch callback order
            return;
          }

          setOptions(newOptions);
          setFetching(false);
          setNascent(false);
        });
      };

      const debounced = debounce(loadOptions, debounceTimeout);

      return (value: string) => {
        setSearch(value);
        if (value.length >= minCharsForSearch) {
          debounced(value);
        } else {
          setOptions([]);
          setNascent(true);
        }
      };
    }, [fetchOptions, debounceTimeout, minCharsForSearch]);

    const clearSearch = useCallback(() => {
      setSearch("");
    }, []);

    return (
      <Select
        ref={ref}
        showSearch
        filterOption={false}
        searchValue={search}
        onDropdownVisibleChange={clearSearch}
        onSearch={debounceFetcher}
        allowClear
        notFoundContent={
          search.length < minCharsForSearch ? (
            <div>Type at least {minCharsForSearch} characters to search</div>
          ) : fetching || nascent ? (
            <Spin size="small" />
          ) : (
            <NoContentFound
              description={
                "Your search returned no results. Expand your search or try different keywords."
              }
            />
          )
        }
        {...props}
        options={createOptionGroups(options)}
      />
    );
  }
);

DebounceSelect.displayName = "DebounceSelect";

export const SearchSelect = React.forwardRef(
  (
    props: {
      fetchOptions: (search: string) => Promise<WebInputChoice[]>;
      minCharsForSearch?: number;
    } & BaseSelectProps,
    ref: React.ForwardedRef<SelectProps<WebInputChoice | WebInputChoice[]>>
  ) => {
    return <DebounceSelect ref={ref} style={{ width: "100%" }} {...props} />;
  }
);
SearchSelect.displayName = "SearchSelect";
